import { harmoniserPresentations } from '../routes/presentation/horsLigne/index.js';
import { harmoniserServiceWorker } from '../horsLigne/serviceWorker.js';
import { modeDemonstrationActif } from '../api/demo.js';
import { persistanceHorsLigne, terminerConsultation } from '../horsLigne/index.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth.js';
import { estSessionIndisponible } from '../api/errors.js';
import type { GalerieSession, SessionUtilisateur } from '../api/types.js';
import {
  adopterIdentite,
  oublierIdentite,
  ouvrirEpoqueDeDroits,
  PREFIXE_METIER,
} from './identite.js';
import type { PersistanceHorsLigne } from '../horsLigne/persistance.js';
import { noterConfinement } from './confinement.js';
import { CLE_SESSION, queryClient } from './queryClient.js';
import { empreinteDroitsGroupe, groupeDeLaGalerie } from './sujet.js';

class SessionObsolete extends Error {
  constructor() {
    super('La session doit être vérifiée de nouveau.');
  }
}

// Preuve liée au résultat brut de la queryFn, partagé par fetchQuery avec
// tous ses appelants. Jamais une génération fraîche attribuée par l'attenteur.
const preuvesSession = new WeakMap<
  SessionUtilisateur,
  {
    persistance: PersistanceHorsLigne;
    generation: number;
  }
>();

/** Fonction commune aux lecteurs de CLE_SESSION ; une preuve par exécution réseau. */
export async function lireSessionVerifiee(): Promise<SessionUtilisateur> {
  if (modeDemonstrationActif()) return authApi.moi();
  const persistance = persistanceHorsLigne();
  const generation = await persistance.commencerVerification();
  // Une identité d'objet distincte par exécution, même si un adaptateur réutilise
  // sa réponse. TanStack peut partager son cache, mais fetchQuery rend le brut.
  const session = { ...(await authApi.moi()) };
  if (generation !== (await persistance.commencerVerification())) throw new SessionObsolete();
  preuvesSession.set(session, { persistance, generation });
  return session;
}

/**
 * Charge (ou lit le cache de) `GET /v1/auth/me`, sans jeter en cas d'échec :
 * les pages appellent `requireSession` pour l'exiger, `useSession` pour la
 * lire quand l'absence de session est un état normal (aucune ici — la régie
 * la redirige toujours vers `/connexion`).
 */
export function useSession() {
  return useQuery({
    queryKey: CLE_SESSION,
    queryFn: lireSessionVerifiee,
  });
}

/**
 * Charge la session pour un chargeur de route ; jette si elle est absente.
 *
 * Toujours RELUE (`staleTime: 0`), jamais servie du cache : après une
 * déconnexion ou la connexion d'un autre compte, la session en cache est
 * celle de l'ancien compte (audit MVP du 23/09, § 2). La session fraîche
 * fixe ensuite l'identité (`adopterIdentite`), qui purge les caches métier
 * si le compte ou la galerie a changé — le tout AVANT le routage.
 *
 * Aucun repli sur le cache mémoire quand `/auth/me` échoue (relecture du 23/09,
 * constats 5 à 7) : une erreur réseau rejette, et le chargeur le dit
 * (`exigerSession`). `networkMode: 'always'` : hors ligne, TanStack mettrait
 * la requête en PAUSE et le chargeur attendrait le réseau indéfiniment. Un
 * refus de l'API (401…) oublie l'identité. M11 autorise seulement le repli
 * sur un instantané chiffré validé, dans exigerSession, sur erreur réseau.
 */
export async function chargerSession(
  galerieSlug: string | null = null,
): Promise<SessionUtilisateur> {
  const precedente = queryClient.getQueryData<SessionUtilisateur>(CLE_SESSION);
  let session: SessionUtilisateur;
  let generationHorsLigne: number | undefined;
  try {
    session = await queryClient.fetchQuery({
      queryKey: CLE_SESSION,
      queryFn: lireSessionVerifiee,
      staleTime: 0,
      networkMode: 'always',
    });
    if (!modeDemonstrationActif()) {
      const preuve = preuvesSession.get(session);
      if (
        !preuve ||
        preuve.persistance !== persistanceHorsLigne() ||
        preuve.generation !== (await preuve.persistance.commencerVerification())
      )
        throw new SessionObsolete();
      generationHorsLigne = preuve.generation;
    }
  } catch (erreur) {
    if (erreur instanceof SessionObsolete)
      await oublierIdentite(queryClient, { localeSeulement: true });
    else if (!estSessionIndisponible(erreur)) await oublierIdentite(queryClient);
    throw erreur;
  }
  // Confinement (CGU, double authentification) : noté AVANT tout le reste —
  // une session confinée n'a plus droit à aucun repli hors ligne, même si la
  // suite échoue.
  noterConfinement(session);
  await adopterIdentite(queryClient, session.compte.id, galerieSlug);
  const droitsChanges = await purgerSiDroitsChanges(precedente, session, galerieSlug);
  if (!modeDemonstrationActif()) {
    // M11 : le serveur vient de parler — on efface tout ce qu'il n'autorise
    // plus (autre compte, autre galerie, module coupé, droits changés).
    terminerConsultation();
    await persistanceHorsLigne().harmoniser(
      session,
      galerieSlug,
      droitsChanges,
      generationHorsLigne,
    );
    await harmoniserPresentations(session, galerieSlug, droitsChanges, precedente);
    void harmoniserServiceWorker(session).catch(() => undefined);
  }
  return session;
}

/**
 * Empreinte des autorisations d'un compte sur une galerie : rôle, droits
 * fins, actions calculées, palier, modules EFFECTIFS (D21), modules en lecture seule et rattachement de
 * groupe applicable (M12). `null` sans accès.
 */
function empreinteDroits(
  session: SessionUtilisateur | undefined,
  galerieSlug: string,
): string | null {
  const galerie = session?.galeries.find((candidate) => candidate.slug === galerieSlug);
  if (!session || !galerie) return null;
  return JSON.stringify([
    galerie.id,
    galerie.role,
    galerie.droits,
    [...galerie.actions].sort(),
    galerie.palier ?? null,
    [...(galerie.modules ?? [])].sort(),
    [...(galerie.modulesLectureSeule ?? [])].sort(),
    empreinteDroitsGroupe(groupeDeLaGalerie(session.compte, galerie)),
  ]);
}

/**
 * Même compte, même galerie, mais autorisations CHANGÉES depuis la session
 * affichée (troisième relecture du 23/09, constat N7) : les données métier de
 * ce compte et de cette galerie ont été lues sous d'autres droits — prix
 * artiste, champs réservés. Elles sont annulées et retirées AVANT tout
 * réaffichage ; les pages les relisent sous les droits actuels.
 */
export async function purgerSiDroitsChanges(
  precedente: SessionUtilisateur | undefined,
  nouvelle: SessionUtilisateur,
  galerieSlug: string | null,
): Promise<boolean> {
  if (!precedente || !galerieSlug || precedente.compte.id !== nouvelle.compte.id) return false;
  if (empreinteDroits(precedente, galerieSlug) === empreinteDroits(nouvelle, galerieSlug))
    return false;
  const portee = [PREFIXE_METIER, nouvelle.compte.id, galerieSlug];
  // D'abord l'époque : les callbacks des mutations lancées sous les anciens
  // droits deviennent obsolètes et n'écriront plus dans le cache purgé.
  ouvrirEpoqueDeDroits();
  await queryClient.cancelQueries({ queryKey: portee });
  queryClient.removeQueries({ queryKey: portee });
  await harmoniserPresentations(nouvelle, galerieSlug, true, precedente);
  return true;
}

/**
 * Après une connexion RÉUSSIE — mot de passe seul, second facteur, ou
 * invitation acceptée : l'identité précédente est définitivement oubliée
 * (requêtes annulées, cache entier vidé, nouvelle époque) AVANT le routage
 * vers la nouvelle session. Rien du compte précédent ne peut plus être
 * servi, même si `/auth/me` échoue ensuite (relecture du 23/09, constat 5).
 */
export function useApresConnexion(): () => Promise<void> {
  const client = useQueryClient();
  return () => oublierIdentite(client);
}

/** Invalide le cache de session après une connexion, une déconnexion, etc. */
export function useInvalidateSession(): () => Promise<void> {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: CLE_SESSION });
}

/** La galerie courante de la session, par son slug (`null` si introuvable). */
export function trouverGalerie(
  session: SessionUtilisateur,
  slug: string | undefined,
): GalerieSession | null {
  if (!slug) return null;
  return session.galeries.find((galerie) => galerie.slug === slug) ?? null;
}
